import React, { createContext, useContext, useReducer, useMemo, useEffect } from 'react';

// Initial state for the context
const initialState = {
  processes: {},
  loading: false,
  error: null,
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  ADD_OR_UPDATE_PROCESS: 'ADD_OR_UPDATE_PROCESS',
  SET_PROCESSES: 'SET_PROCESSES', // Potentially useful for bulk updates or resets
};


// Reducer function to manage state updates
function processReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ActionTypes.ADD_OR_UPDATE_PROCESS:
      let processData = action.payload;
      // If process is stopped and missing fields, fill defaults
      if (processData?.state === 'stopped') {
        if (!processData.Process) processData.Process = {};
        if (!processData.Process.process_id) processData.Process.process_id = 'NA';
        if (!processData.Process.process_name) processData.Process.process_name = 'N/A';
        if (!processData.Process.memory_usage) processData.Process.memory_usage = 0;
        if (!processData.Process.cpu_usage) processData.Process.cpu_usage = 0;
        if (!processData.Process.disk_usage) processData.Process.disk_usage = 0;
        // Add more defaults as needed
        processData.ui_state = 'inactive';
      } else {
        processData.ui_state = processData.state;
      }
      // Always use Process.name as the unique key for all processes
      // This ensures that updates (active/inactive) overwrite the previous entry
      const monitorName = processData?.Process?.name;
      if (!monitorName) {
        console.error('Received process data without a valid Process.name:', processData);
        return state; // Don't modify state if name is missing
      }
      // Store/update the process in state keyed by monitor name
      return {
        ...state,
        processes: {
          ...state.processes,
          [monitorName]: processData,
        },
        error: null, // Clear error on successful update
      };

    case ActionTypes.SET_PROCESSES:
      // Expects an object keyed by process_id
      return {
        ...state,
        processes: action.payload,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
}

// Create the context
const ProcessContext = createContext();

// Provider component
export const ProcessProvider = ({ children }) => {
  const [state, dispatch] = useReducer(processReducer, initialState);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    ...state,
    // Provide dispatch actions directly (or wrap them in functions)
    addOrUpdateProcess: (process) => dispatch({ type: ActionTypes.ADD_OR_UPDATE_PROCESS, payload: process }),
    setLoading: (isLoading) => dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading }),
    setError: (error) => dispatch({ type: ActionTypes.SET_ERROR, payload: error }),
  }), [state, dispatch]);

  // Add event listener for process-update events from WebSocketContext
  useEffect(() => {
    const handleProcessEvent = (event) => {
      if (event.detail && event.detail.data) {
        // Process update events happen frequently - no need to log them
        dispatch({ 
          type: ActionTypes.ADD_OR_UPDATE_PROCESS, 
          payload: event.detail.data 
        });
      }
    };
    
    // Listen for the custom process-update event dispatched by WebSocketContext
    window.addEventListener('process-update', handleProcessEvent);
    
    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('process-update', handleProcessEvent);
    };
  }, []);

  return (
    <ProcessContext.Provider value={contextValue}>
      {children}
    </ProcessContext.Provider>
  );
};

// Custom hook to use the context
export const useProcessContext = () => {
  const context = useContext(ProcessContext);
  if (context === undefined) {
    throw new Error('useProcessContext must be used within a ProcessProvider');
  }
  return context;
};
